import React, { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import API from "../../api/api";
import { FaPhoneAlt, FaEnvelope, FaSearch, FaUserTie, FaMapMarkerAlt } from "react-icons/fa";

const DistrictContactsModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState({ districts: [] });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    setLoading(true);
    API.fetchDistrictContacts()
      .then((res) => {
        if (isMounted) {
          setData({
            districts: res?.districts || [],
          });
        }
      })
      .catch((err) => {
        console.error("Failed to load district contacts:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const filteredDistricts = useMemo(() => {
    if (!searchTerm.trim()) return data.districts;
    const q = searchTerm.toLowerCase().trim();
    return data.districts.filter(
      (d) =>
        (d.district_name && d.district_name.toLowerCase().includes(q)) ||
        (d.official_name && d.official_name.toLowerCase().includes(q)) ||
        (d.mobile && String(d.mobile).includes(q)) ||
        (d.email && d.email.toLowerCase().includes(q))
    );
  }, [data.districts, searchTerm]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="District Para Sports Associations - Official Contacts"
      width="900px"
    >
      <div className="space-y-4">
        {/* Important Notice Banner */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-4 rounded-xl shadow-xs">
          <div className="flex items-start gap-3">
            <span className="text-xl">⚠️</span>
            <div className="text-xs text-amber-900 leading-relaxed">
              <strong className="font-bold text-amber-950 block text-sm mb-1">
                Mandatory Prior Intimation Notice
              </strong>
              Before registering as a player or applying for any event/game, all players{" "}
              <strong className="underline">must contact their corresponding District Secretary or President</strong> for
              prior intimation. District authorization is mandatory for verification and approval; otherwise, registrations
              will not be accepted.
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            type="text"
            placeholder="Search by District name, Secretary/President name, or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:bg-white focus:outline-none transition shadow-xs"
          />
        </div>

        {/* District Directory Grid */}
        {loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            Loading district contact details...
          </div>
        ) : filteredDistricts.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-sm">
            No matching district found. Try a different search keyword.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1">
            {filteredDistricts.map((item) => (
              <div
                key={item.district_id || item.district_name}
                className="bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm p-3.5 rounded-xl transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-slate-100">
                    <span className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                      <FaMapMarkerAlt className="text-rose-500 shrink-0 text-xs" />
                      {item.district_name}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      {item.state || "AP"}
                    </span>
                  </div>

                  <div className="space-y-1 mb-3">
                    <p className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                      <FaUserTie className="text-slate-400 shrink-0" />
                      {item.official_name || "District Secretary"}
                    </p>
                    <p className="text-[11px] text-slate-500 pl-5">
                      {item.designation || "District Secretary"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-slate-100 mt-auto">
                  {item.mobile ? (
                    <a
                      href={`tel:${item.mobile}`}
                      className="flex-1 py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition shadow-2xs"
                    >
                      <FaPhoneAlt className="text-[11px]" /> {item.mobile}
                    </a>
                  ) : (
                    <span className="flex-1 text-center py-1.5 text-[11px] text-slate-400 italic">
                      Contact via Secretariat
                    </span>
                  )}
                  {item.email && (
                    <a
                      href={`mailto:${item.email}`}
                      title={item.email}
                      className="py-1.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition"
                    >
                      <FaEnvelope className="text-[11px]" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pt-2 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition"
          >
            Close Directory
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DistrictContactsModal;
